import React, { useState, useRef, useEffect } from "react";
import Papa from "papaparse";
import {
	Upload,
	FileSpreadsheet,
	CircleX,
	Download,
	CircleCheck
} from "lucide-react";
import {
	UploadContainer,
	FileInput,
	UploadButton,
	ValidationResults,
	ErrorMessage,
	MessageHeader,
	ErrorTitle,
	ErrorListContainer,
	ErrorListItem,
	ErrorListFooter,
	SuccessMessage,
	SuccessTitle,
	TextContainer,
	FileName,
	FileSize,
	UploadArea,
	FileInfoArea,
	FileInfo,
	DownloadErrorsButton,
	SearchSelect,
	ButtonsContainer,
	StepButton
} from "./styles";
import { useSelector, useDispatch } from "react-redux";
import {
	getCompanies,
	getHiredList
} from "../../../Company/containers/Report/actions";
import _ from "lodash";

import {
	validateCSVStructure,
	validateField,
	mapCSVValue,
	CONTRACT_TYPE_MAPPING,
	MODALITY_MAPPING,
	PRIORITY_MAPPING,
	TIER_MAPPING
} from "./validate";
import dayjs from "dayjs";
import XLSX from "xlsx-js-style";
import { cleanCpfCnpj, stringToMask } from "helpers/string";

const CSV_FIELDS = [
	"RAZAO SOCIAL",
	"CNPJ",
	"TIPO DE CONTRATO",
	"FUNCAO",
	"ATIVIDADES REALIZADAS",
	"MODALIDADE",
	"REMUNERACAO",
	"DATA DO PAGAMENTO",
	"VIGENCIA",
	"DATA DE INICIO",
	"PRIORIDADE",
	"TIER",
	"CLAUSULAS E INFORMACOES ADICIONAIS"
];

const CSV_FIELD_MAPPING = {
	"RAZAO SOCIAL": "businessName",
	CNPJ: "document",
	"TIPO DE CONTRATO": "contractType",
	FUNCAO: "role",
	"ATIVIDADES REALIZADAS": "activitiesDescription",
	MODALIDADE: "modality",
	REMUNERACAO: "value",
	"DATA DO PAGAMENTO": "paymentDate",
	VIGENCIA: "lifespan",
	"DATA DE INICIO": "startDate",
	PRIORIDADE: "priority",
	TIER: "tier",
	"CLAUSULAS E INFORMACOES ADICIONAIS": "additionalInformation"
};

const CSVImportSolicitationContract = ({
	onCSVData,
	onGoBack,
	onSubmit,
	validationErrors,
	selectedFile
}) => {
	const fileInputRef = useRef(null);
	const [isProcessing, setIsProcessing] = useState(false);
	const [file, setFile] = useState(selectedFile);
	const [errors, setErrors] = useState(validationErrors || []);
	const [selectedHirer, setSelectedHirer] = useState(null);
	const [isLoadingHirers, setIsLoadingHirers] = useState(false);
	const [isLoadingHireds, setIsLoadingHireds] = useState(false);
	const [processedData, setProcessedData] = useState([]);

	const dispatch = useDispatch();
	const { items: companies, hireds } = useSelector(s => s.company);

	let findHirerList = null;
	let findHiredList = null;

	const handleFindHirerList = businessName => {
		if (findHirerList) {
			findHirerList.cancel();
		}

		let filterStr = "&filters[isHired]=true";
		if (businessName) filterStr += `&filters[businessName]=${businessName}`;

		setIsLoadingHirers(true);

		findHirerList = _.debounce(
			() =>
				dispatch(
					getCompanies(0, 10, filterStr, "createdAt", true, false, err => {
						setIsLoadingHirers(false);
						if (err) {
							alert(
								"Não foi possível carregar os dados, erro: " + typeof err ===
									"string"
									? err
									: "Erro: " + JSON.stringify(err)
							);
						}
					})
				),
			500
		);

		findHirerList();
	};

	const handleFindHiredList = (hirerId, businessName = null) => {
		if (!hirerId) {
			return;
		}

		if (findHiredList) {
			findHiredList.cancel();
		}

		let filterStr = "";
		if (businessName) filterStr += `&filters[businessName]=${businessName}`;

		setIsLoadingHireds(true);

		findHiredList = _.debounce(
			() =>
				dispatch(
					getHiredList(
						hirerId,
						filterStr,
						err => {
							setIsLoadingHireds(false);
							if (err) {
								alert(
									"Não foi possível carregar os dados, erro: " + typeof err ===
										"string"
										? err
										: "Erro: " + JSON.stringify(err)
								);
							}
						},
						1000
					)
				),
			500
		);

		findHiredList();
	};

	useEffect(() => {
		if (selectedHirer) {
			handleFindHiredList(selectedHirer.value);
		}
	}, [selectedHirer]);

	const groupErrorsByRow = errors => {
		const grouped = {};
		errors.forEach(error => {
			if (!grouped[error.row]) {
				grouped[error.row] = [];
			}
			grouped[error.row].push(error);
		});
		return grouped;
	};

	const generateCSVWithErrors = data => {
		const headers = [...CSV_FIELDS, "ERROS"];

		const csvData = data.map(row => {
			const csvRow = {};
			CSV_FIELDS.forEach(field => {
				csvRow[field] = row[field] || "";
			});
			csvRow["ERROS"] = row.erro || "";
			return csvRow;
		});

		const csv = Papa.unparse({
			fields: headers,
			data: csvData
		});

		return csv;
	};

	const downloadCSVWithErrors = () => {
		const csvContent = generateCSVWithErrors(processedData);
		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);

		link.setAttribute("href", url);
		link.setAttribute(
			"download",
			`arquivo_com_erros_${new Date().getTime()}.csv`
		);
		link.style.visibility = "hidden";

		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const handleFileUpload = event => {
		if (!selectedHirer) {
			setErrors(["Selecione um contratante antes de enviar o arquivo"]);
			return;
		}

		setFile(null);

		const uploadedFile = event.target.files[0];
		if (!uploadedFile) return;

		setFile(uploadedFile);
		setIsProcessing(true);

		const reader = new FileReader();

		reader.onload = e => {
			const data = new Uint8Array(e.target.result);
			const workbook = XLSX.read(data, { type: "array" });
			const sheetName = workbook.SheetNames[0];
			const worksheet = workbook.Sheets[sheetName];
			const csv = XLSX.utils.sheet_to_csv(worksheet, { blankrows: false });

			Papa.parse(csv, {
				header: true,
				skipEmptyLines: true,
				complete: results => {
					const structureErrors = validateCSVStructure(
						results.meta.fields,
						results.data
					);

					if (structureErrors.length > 0) {
						setErrors(structureErrors);
						setProcessedData([]);
						onCSVData([], structureErrors, uploadedFile, selectedHirer);
						setIsProcessing(false);
						return;
					}

					const validationErrors = [];
					const validatedData = results.data.map((row, index) => {
						const rowErrors = [];
						const mappedRow = {};
						let hired = "";

						Object.keys(row).forEach(field => {
							if (CSV_FIELDS.includes(field)) {
								const originalFieldName = CSV_FIELD_MAPPING[field] || field;
								let fieldValue = row[field];

								if (field === "TIPO DE CONTRATO") {
									fieldValue = mapCSVValue(
										field,
										row[field],
										CONTRACT_TYPE_MAPPING
									);
								} else if (field === "MODALIDADE") {
									fieldValue = mapCSVValue(field, row[field], MODALITY_MAPPING);
								} else if (field === "PRIORIDADE") {
									fieldValue = mapCSVValue(field, row[field], PRIORITY_MAPPING);
								} else if (field === "TIER") {
									fieldValue = mapCSVValue(field, row[field], TIER_MAPPING);
								} else if (field === "CNPJ") {
									if (!hired) {
										hired = hireds.find(
											h => cleanCpfCnpj(h.cnpj) === cleanCpfCnpj(row[field])
										)?._id;
									}
								} else if (field === "RAZAO SOCIAL") {
									if (!hired) {
										hired = hireds.find(h => h.businessName === row[field])
											?._id;
									}
								} else if (field === "DATA DE INICIO") {
									fieldValue = dayjs(fieldValue, "DD/MM/YYYY").format(
										"DD/MM/YYYY"
									);
								} else if (field === "REMUNERACAO") {
									fieldValue = fieldValue.replace("R$", "");
								}

								mappedRow[originalFieldName] = fieldValue;

								const errorMessage = validateField(
									field,
									row[field],
									index,
									hireds
								);
								if (errorMessage) {
									const error = {
										row: index + 2,
										column: field,
										message: errorMessage
									};
									rowErrors.push(error);
									validationErrors.push(error);
								}
							}
						});

						return {
							...mappedRow,
							...row,
							hired: hired,
							erro:
								rowErrors.length > 0
									? rowErrors.map(e => `${e.column}: ${e.message}`).join("; ")
									: "",
							_isValid: rowErrors.length === 0
						};
					});

					setProcessedData(validatedData);
					setErrors(validationErrors);
					onCSVData(
						validatedData,
						validationErrors,
						uploadedFile,
						selectedHirer
					);
					setIsProcessing(false);
				},
				error: error => {
					setErrors(["Erro ao processar arquivo CSV: " + error.message]);
					setIsProcessing(false);
				}
			});
		};

		reader.readAsArrayBuffer(uploadedFile);
	};

	const formatFileSize = bytes => {
		if (bytes === 0) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
	};

	const hirerOptions = companies.map(company => ({
		value: company._id,
		label: `${company.businessName} - ${stringToMask(company.cnpj, "cnpj")}`
	}));

	const groupedErrors = groupErrorsByRow(errors);

	return (
		<>
			<UploadContainer>
				<div style={{ marginBottom: "20px" }}>
					<label
						htmlFor="hirer-select"
						style={{
							display: "block",
							marginBottom: "8px",
							fontSize: "14px",
							color: "#31475A"
						}}
					>
						Contratante
					</label>
					<SearchSelect
						id="hirer-select"
						options={hirerOptions}
						isLoading={isLoadingHirers}
						onInputChange={handleFindHirerList}
						onChange={selectedOption => {
							setSelectedHirer(selectedOption);
							setErrors([]);
							setFile(null);
							setProcessedData([]);
						}}
						placeholder="Buscar o contratante"
						noOptionsMessage={() => "Buscar o contratante"}
						isDisabled={isProcessing}
					/>
					{isLoadingHireds && (
						<p style={{ marginTop: "8px", color: "#666", fontSize: "12px" }}>
							Carregando prestadores...
						</p>
					)}
					{selectedHirer && hireds && hireds.length > 0 && (
						<p style={{ marginTop: "8px", color: "#28a745", fontSize: "12px" }}>
							{hireds.length} prestador(es) encontrado(s) para este contratante
						</p>
					)}
				</div>

				{!file ? (
					<UploadArea>
						<FileInput
							ref={fileInputRef}
							type="file"
							accept=".xlsx"
							onChange={handleFileUpload}
							id="csv-upload"
							disabled={!selectedHirer || isProcessing}
						/>
						<UploadButton
							htmlFor="csv-upload"
							disabled={!selectedHirer || isProcessing}
							allowed={!!selectedHirer}
						>
							<Upload size={32} />
							Clique para fazer upload da planilha
						</UploadButton>
						{!selectedHirer && (
							<p
								style={{
									marginTop: "10px",
									color: "#dc3545",
									fontSize: "12px"
								}}
							>
								Selecione um contratante primeiro
							</p>
						)}
						<p
							style={{ marginTop: "10px", color: "#9F9FA9", fontSize: "12px" }}
						>
							Apenas arquivos .XLSX são aceitos
						</p>
					</UploadArea>
				) : (
					<div>
						<UploadArea>
							<FileInput
								ref={fileInputRef}
								type="file"
								accept=".xlsx"
								onChange={handleFileUpload}
								id="csv-upload"
								disabled={isProcessing}
							/>
							<UploadButton
								htmlFor="csv-upload"
								disabled={!selectedHirer || isProcessing}
								allowed={!!selectedHirer}
							>
								<Upload size={32} />
								{isProcessing ? "Processando..." : "Trocar arquivo"}
							</UploadButton>
						</UploadArea>
						<FileInfoArea>
							<FileSpreadsheet color="#008236" size={24} />
							<FileInfo>
								<FileName>{file.name}</FileName>
								<FileSize>{formatFileSize(file.size)}</FileSize>
							</FileInfo>
						</FileInfoArea>

						<ValidationResults>
							{errors.length === 0 ? (
								<SuccessMessage>
									<MessageHeader>
										<CircleCheck color="#008236" size={24} />
										<TextContainer>
											<SuccessTitle bold>
												Todos os dados foram validados com sucesso!
											</SuccessTitle>
											<SuccessTitle>
												{processedData?.length} registro(s) prontos para
												processamento.
											</SuccessTitle>
										</TextContainer>
									</MessageHeader>
								</SuccessMessage>
							) : (
								<ErrorMessage>
									<MessageHeader>
										<CircleX size={20} color="#C70036" />
										<ErrorTitle>
											Foram encontrados {errors.length} erro(s) na planilha:
										</ErrorTitle>
									</MessageHeader>
									<ErrorListContainer>
										{Object.entries(groupedErrors)
											.slice(0, 2)
											.map(([rowNumber, rowErrors]) => (
												<ErrorListItem key={rowNumber}>
													<div
														style={{ fontWeight: "bold", marginBottom: "4px" }}
													>
														Linha {rowNumber}:
													</div>
													{rowErrors.map((error, index) => (
														<div
															key={index}
															style={{ marginLeft: "12px", fontSize: "10px" }}
														>
															Coluna &quot;{error.column}&quot;: {error.message}
														</div>
													))}
												</ErrorListItem>
											))}
										{Object.keys(groupedErrors).length > 2 && (
											<ErrorListFooter>
												... e mais {Object.keys(groupedErrors).length - 2}{" "}
												linha(s) com erro(s)
											</ErrorListFooter>
										)}
									</ErrorListContainer>

									<DownloadErrorsButton onClick={downloadCSVWithErrors}>
										<Download size={12} color="#C70036" />
										Baixar registro com todos os erros
									</DownloadErrorsButton>
								</ErrorMessage>
							)}
						</ValidationResults>
					</div>
				)}
			</UploadContainer>
			<ButtonsContainer>
				<StepButton onClick={() => onGoBack("start")}>Voltar</StepButton>
				{!errors.length && file && selectedHirer && (
					<StepButton
						primary
						onClick={() => {
							onSubmit();
						}}
					>
						Processar planilha
					</StepButton>
				)}
			</ButtonsContainer>
		</>
	);
};

export default CSVImportSolicitationContract;
